import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { FREQ_OPTIONS, FREQ_DEFAULTS, FrequencyConfig } from './CareCatalogPanel';
import AttachmentPickerSheet from '../shared/AttachmentPickerSheet';
import MultiDatePicker from './MultiDatePicker';

const CURRENCIES = ['USD','EUR','GBP','CAD','AUD','NZD','ZAR','CHF','JPY','HKD'];

function localNow() {
  const now = new Date();
  return {
    date: now.toLocaleDateString('sv'),
    time: now.toTimeString().slice(0, 5),
  };
}

export default function ScheduleCareModal({ open, onClose, onSaved, horse, locations, catalogItems, editing, prefillOverride }) {
  const { date: todayDate, time: nowTime } = localNow();

  // Travel-specific state
  const [travelDirection, setTravelDirection]       = useState('');
  const [travelDestType, setTravelDestType]         = useState('boarding_farm');
  const [travelDestEntityId, setTravelDestEntityId] = useState('');
  const [travelDestOtherText, setTravelDestOther]   = useState('');
  const [boardingFacilities, setBoardingFacilities] = useState([]);
  const [trainerFacilities, setTrainerFacilities]   = useState([]);

  const initForm = () => ({
    horse_id: horse?.id,
    care_catalog_item_id: null,
    category: '',
    type: '',
    name: '',
    dosage: '',
    dosage_unit: '',
    units: '',
    start_date: '',
    preferred_time: '',
    end_date: '',
    end_date_type: 'fixed',       // 'fixed' | 'relative'
    end_date_rel_value: '',       // e.g. '30'
    end_date_rel_unit: 'days',    // 'days' | 'weeks' | 'months' | 'years'
    location_id: '',
    is_routine: false,
    frequency_type: 'daily',
    frequency_config: { ...FREQ_DEFAULTS.daily },
    billable: false,
    cost: '',
    cost_currency: 'USD',
    notes: '',
    routine_slot: '',
    is_breeding: false,
  });

  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  // Multi-date support for Travel scheduling
  const [travelDates, setTravelDates] = useState([]);

  useEffect(() => {
    if (!open) return;
    setAttachments([]);
    // Load CRM entities for travel destination picker
    api.get('/crm-entities').then(res => {
      const all = res.data || [];
      setBoardingFacilities(all.filter(e => ['farm_boarding','farm'].includes(e.organization_industry_role)));
      setTrainerFacilities(all.filter(e => e.organization_industry_role === 'trainer' || e.person_industry_role === 'trainer'));
    }).catch(() => {});
    // Initialise travel dates with today
    setTravelDates([{ id: Math.random().toString(36).slice(2), date: todayDate, time: nowTime }]);
    if (editing) {
      const ft = editing.frequency_type || 'daily';
      setForm({
        horse_id: editing.horse_id,
        care_catalog_item_id: editing.care_catalog_item_id || null,
        category: editing.category || '',
        type: editing.type || '',
        name: editing.name || '',
        dosage: editing.dosage || '',
        dosage_unit: editing.dosage_unit || '',
        units: editing.units ?? '',
        start_date: editing.start_date || todayDate,
        preferred_time: editing.preferred_time || '',
        end_date: editing.end_date || '',
        end_date_type: editing.end_date_type || 'fixed',
        end_date_rel_value: editing.end_date_rel_value || '',
        end_date_rel_unit: editing.end_date_rel_unit || 'days',
        location_id: editing.location_id || '',
        is_routine: editing.is_routine || false,
        frequency_type: ft,
        frequency_config: { ...(FREQ_DEFAULTS[ft] || {}), ...(editing.frequency_config || {}) },
        billable: editing.billable || false,
        cost: editing.cost ?? '',
        cost_currency: editing.cost_currency || 'USD',
        notes: editing.notes || '',
        routine_slot: editing.routine_slot || '',
        is_breeding: editing.is_breeding || false,
      });
      // Restore travel fields
      setTravelDirection(editing.travel_direction || '');
      setTravelDestType(editing.travel_dest_type || 'boarding_farm');
      setTravelDestEntityId(editing.travel_dest_entity_id || '');
      setTravelDestOther(editing.travel_dest_other || '');
    } else if (prefillOverride) {
      const ft = prefillOverride.frequency_type || 'daily';
      setForm({
        ...initForm(),
        category: prefillOverride.category || '',
        type: prefillOverride.type || '',
        name: prefillOverride.name || '',
        dosage: prefillOverride.dosage || '',
        location_id: prefillOverride.location_id || '',
        notes: prefillOverride.notes || '',
        frequency_type: ft,
        frequency_config: { ...(FREQ_DEFAULTS[ft] || {}), ...(prefillOverride.frequency_config || {}) },
        is_routine: prefillOverride.is_routine || true,
      });
    } else {
      setForm(initForm());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, prefillOverride, horse]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const computeEndDate = () => {
    if (form.end_date_type === 'fixed') return form.end_date || null;
    if (!form.end_date_rel_value || !form.start_date) return null;
    const d = new Date(form.start_date + 'T12:00:00');
    const val = parseInt(form.end_date_rel_value, 10);
    if (form.end_date_rel_unit === 'days')   d.setDate(d.getDate() + val);
    if (form.end_date_rel_unit === 'weeks')  d.setDate(d.getDate() + val * 7);
    if (form.end_date_rel_unit === 'months') d.setMonth(d.getMonth() + val);
    if (form.end_date_rel_unit === 'years')  d.setFullYear(d.getFullYear() + val);
    return d.toLocaleDateString('sv');
  };

  // Derived catalog filters
  const catalogCategories = [...new Set((catalogItems || []).map(c => c.category))].filter(Boolean).sort();
  const catalogTypes      = [...new Set((catalogItems || []).filter(c => c.category === form.category).map(c => c.type))].filter(Boolean).sort();
  const filteredCatalog   = (catalogItems || []).filter(c =>
    c.category === form.category && (!form.type || c.type === form.type)
  );
  const selectedItem      = filteredCatalog.find(c => c.id === form.care_catalog_item_id);

  const handleNameSelect = (itemId) => {
    if (itemId === '__none__') {
      setForm(p => ({ ...p, care_catalog_item_id: null, name: '', dosage: '', dosage_unit: '', units: '' }));
      return;
    }
    const item = filteredCatalog.find(c => c.id === itemId);
    if (item) {
      const ft = item.frequency_type || 'daily';
      setForm(p => ({
        ...p,
        care_catalog_item_id: item.id,
        name: item.name,
        dosage: item.dosage || '',
        dosage_unit: item.dosage_unit || '',
        units: item.units ?? (item.serving_size ?? ''),
        is_routine: item.routine || p.is_routine,
        frequency_type: ft,
        frequency_config: { ...(FREQ_DEFAULTS[ft] || {}), ...(item.frequency_config || {}) },
        billable: item.billable || false,
        cost: item.cost ?? '',
        cost_currency: item.cost_currency || p.cost_currency || 'USD',
        is_breeding: item.is_breeding || false,
      }));
    }
  };

  const getTravelDestName = () => {
    if (travelDirection === 'to_farm') return 'On Farm';
    if (travelDirection === 'from_farm') {
      if (travelDestType === 'boarding_farm') return boardingFacilities.find(b => b.id === travelDestEntityId)?.system_name || '';
      if (travelDestType === 'with_trainer') return trainerFacilities.find(t => t.id === travelDestEntityId)?.system_name || '';
      return travelDestOtherText;
    }
    return travelDestOtherText;
  };

  const handleSave = async () => {
    const isTravel = form.category === 'Travel';
    if (isTravel) {
      if (!travelDirection) { toast.error('Travel direction is required'); return; }
      if (travelDirection === 'from_farm' && (travelDestType !== 'other') && !travelDestEntityId) { toast.error('Destination is required'); return; }
      if (travelDirection === 'from_farm' && travelDestType === 'other' && !travelDestOtherText.trim()) { toast.error('Destination is required'); return; }
      if (!editing && (!travelDates.length || !travelDates[0].date)) { toast.error('Date is required'); return; }
    } else {
      const entryName = form.name || selectedItem?.name || '';
      if (!form.category) { toast.error('Category is required'); return; }
      if (!form.type)     { toast.error('Type is required'); return; }
      if (!entryName.trim()) { toast.error('Name is required'); return; }
      if (!form.start_date)  { toast.error('Date is required'); return; }
    }
    setSaving(true);
    try {
      const destName = isTravel ? getTravelDestName() : '';
      const travelName = isTravel
        ? (travelDirection === 'to_farm' ? `On Farm → Departure` : travelDirection === 'from_farm' ? `On Farm → ${destName}` : `Travel: ${destName}`)
        : (form.name || selectedItem?.name || '');

      const payload = {
        ...form,
        name: travelName || form.name || selectedItem?.name || '',
        units: form.units !== '' ? parseFloat(form.units) : null,
        cost: form.billable && form.cost !== '' ? parseFloat(form.cost) || null : null,
        end_date: isTravel ? '' : computeEndDate(),
        location_name: locations?.find(l => l.id === form.location_id)?.name || '',
        frequency_type: (isTravel || !form.is_routine) ? 'once' : form.frequency_type,
        frequency_config: (isTravel || !form.is_routine) ? {} : form.frequency_config,
        is_routine: isTravel ? false : form.is_routine,
        // Travel-specific fields
        ...(isTravel && {
          type: 'Transport',
          travel_direction: travelDirection,
          travel_dest_type: travelDestType,
          travel_dest_entity_id: (travelDirection === 'from_farm' && travelDestType !== 'other') ? travelDestEntityId : '',
          travel_dest_other: travelDestOtherText,
          travel_destination_name: destName,
        }),
      };
      if (editing) {
        await api.put(`/farmfolio/scheduled-care/${editing.id}`, payload);
        toast.success('Schedule updated');
      } else if (isTravel && travelDates.length > 1) {
        // Multiple travel dates — create one scheduled record per date
        await Promise.all(travelDates.map(d =>
          api.post('/farmfolio/scheduled-care', { ...payload, start_date: d.date, preferred_time: d.time || '' })
        ));
        toast.success(`${travelDates.length} travel events scheduled`);
      } else {
        const singleDate = isTravel ? (travelDates[0]?.date || form.start_date) : form.start_date;
        const singleTime = isTravel ? (travelDates[0]?.time || form.preferred_time) : form.preferred_time;
        await api.post('/farmfolio/scheduled-care', { ...payload, start_date: singleDate, preferred_time: singleTime });
        toast.success('Care scheduled');
      }

      if (attachments.length > 0) {
        setUploading(true);
        await Promise.allSettled(
          attachments.map(({ file }) => {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('horse_id', horse.id);
            fd.append('description', `Scheduled ${form.name || selectedItem?.name || ''} — ${form.start_date}${form.preferred_time ? ' @ ' + form.preferred_time : ''}`);
            return api.post('/files/upload', fd);
          })
        );
        setUploading(false);
      }

      onSaved();
      onClose();
    } catch { toast.error('Failed to save schedule'); }
    setSaving(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files.map(file => ({ name: file.name, file }))]);
    e.target.value = '';
  };

  const showQuantity = ['Medical', 'Daily Care', 'Conditioning'].includes(form.category);

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Scheduled Care' : 'Schedule Care'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Horse (read-only in edit mode) */}
          {editing && (
            <div className="space-y-1">
              <Label className="font-semibold">Horse</Label>
              <Input
                value={horse?.system_name || horse?.name || editing?.horse_name || '—'}
                readOnly
                disabled
                className="bg-muted/30 cursor-not-allowed opacity-70"
                data-testid="schedule-care-horse-readonly"
              />
            </div>
          )}

          {/* Category */}
          <div className="space-y-1">
            <Label className="font-semibold">Category</Label>
            <Select value={form.category || '__none__'} onValueChange={v => {
              const val = v === '__none__' ? '' : v;
              setForm(p => ({ ...p, category: val, type: '', care_catalog_item_id: null, name: '', dosage: '', dosage_unit: '', units: '' }));
              if (val === 'Travel') { setTravelDirection(''); setTravelDestEntityId(''); setTravelDestOther(''); }
            }}>
              <SelectTrigger data-testid="schedule-care-category"><SelectValue placeholder="Select category…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {catalogCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                {!catalogCategories.includes('Travel') && <SelectItem value="Travel">Travel</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          {/* Travel-specific fields */}
          {form.category === 'Travel' && (
            <div className="space-y-3 border rounded p-3 bg-indigo-50/30">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Travel Details</p>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Direction</Label>
                <div className="flex gap-2">
                  {[{ v: 'from_farm', l: 'Leaving Farm' }, { v: 'to_farm', l: 'Returning to Farm' }, { v: 'other', l: 'Other' }].map(b => (
                    <button key={b.v} type="button"
                      onClick={() => { setTravelDirection(b.v); setTravelDestEntityId(''); setTravelDestOther(''); }}
                      className={`flex-1 px-2 py-1.5 rounded text-xs font-medium border transition-colors ${travelDirection === b.v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                      {b.l}
                    </button>
                  ))}
                </div>
              </div>

              {travelDirection === 'from_farm' && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Destination Type</Label>
                  <Select value={travelDestType} onValueChange={v => { setTravelDestType(v); setTravelDestEntityId(''); }}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boarding_farm">Boarding Farm</SelectItem>
                      <SelectItem value="with_trainer">With Trainer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {(travelDestType === 'boarding_farm') && (
                    <Select value={travelDestEntityId || '__none__'} onValueChange={v => setTravelDestEntityId(v === '__none__' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select farm…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Select —</SelectItem>
                        {boardingFacilities.map(b => <SelectItem key={b.id} value={b.id}>{b.system_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {(travelDestType === 'with_trainer') && (
                    <Select value={travelDestEntityId || '__none__'} onValueChange={v => setTravelDestEntityId(v === '__none__' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select trainer…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Select —</SelectItem>
                        {trainerFacilities.map(t => <SelectItem key={t.id} value={t.id}>{t.system_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {travelDestType === 'other' && (
                    <Input className="h-8 text-xs" placeholder="Destination name…" value={travelDestOtherText} onChange={e => setTravelDestOther(e.target.value)} />
                  )}
                </div>
              )}

              {(travelDirection === 'other') && (
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Destination</Label>
                  <Input className="h-8 text-xs" placeholder="e.g. Keeneland Racetrack" value={travelDestOtherText} onChange={e => setTravelDestOther(e.target.value)} />
                </div>
              )}

              {/* Vanned By */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Vanned By</Label>
                <Input className="h-8 text-xs" placeholder="e.g. Van Co." value={form.notes} onChange={e => f('notes', e.target.value)} />
              </div>
            </div>
          )}

          {/* Type / Name / Quantity — hidden for Travel */}
          {form.category !== 'Travel' && (<>
          {/* Type */}
          <div className="space-y-1">
            <Label className="font-semibold">Type</Label>
            <Select value={form.type || '__none__'} onValueChange={v => {
              const val = v === '__none__' ? '' : v;
              setForm(p => ({ ...p, type: val, care_catalog_item_id: null, name: '', dosage: '', dosage_unit: '', units: '' }));
            }} disabled={!form.category}>
              <SelectTrigger data-testid="schedule-care-type"><SelectValue placeholder={form.category ? 'Select type…' : 'Select category first'} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {catalogTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Name */}
          <div className="space-y-1">
            <Label className="font-semibold">Name</Label>
            <Select value={form.care_catalog_item_id || '__none__'} onValueChange={handleNameSelect} disabled={!form.type}>
              <SelectTrigger data-testid="schedule-care-name">
                <SelectValue placeholder={form.type ? (filteredCatalog.length ? 'Select from catalog…' : 'No catalog items') : 'Select type first'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {filteredCatalog.map(item => {
                  const detail = item.dosage
                    ? `${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''}`
                    : item.serving_size != null
                      ? `${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''}`
                      : null;
                  return (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}{detail ? ` (${detail})` : ""}{ (item.is_billable ?? item.billable) ? " (billable)" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Quantity */}
          {showQuantity && (
            <div className="space-y-1">
              <Label className="font-semibold">Quantity</Label>
              <Input type="number" min="0" step="0.01" value={form.units}
                onChange={e => f('units', e.target.value)} placeholder="e.g. 1, 2.5" />
            </div>
          )}
          </>)}

          {/* Date — MultiDatePicker for new Travel, single input for all others / edit mode */}
          {form.category === 'Travel' && !editing ? (
            <div className="space-y-1">
              <Label className="font-semibold">Date(s)</Label>
              <MultiDatePicker value={travelDates} onChange={setTravelDates} defaultTime={nowTime} />
            </div>
          ) : (
          <div className="space-y-1">
            <Label className="font-semibold">Date</Label>
            <Input type="date" value={form.start_date} onChange={e => f('start_date', e.target.value)}
              className={form.start_date ? 'date-filled' : ''} data-testid="schedule-care-date" />
          </div>
          )}

          {/* Time — hidden for new Travel (MultiDatePicker includes time per row) */}
          {!(form.category === 'Travel' && !editing) && (
          <div className="space-y-1">
            <Label className="font-semibold">Time</Label>
            <Input type="time" value={form.preferred_time} onChange={e => f('preferred_time', e.target.value)}
              className={form.preferred_time ? 'time-filled' : ''} data-testid="schedule-care-time" />
          </div>
          )}

          {/* Location */}
          <div className="space-y-1">
            <Label className="font-semibold">Location</Label>
            <Select value={form.location_id || '__none__'} onValueChange={v => f('location_id', v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Notes — hidden for Travel (Vanned By is used instead) */}
          {form.category !== 'Travel' && (
          <div className="space-y-1">
            <Label className="font-semibold">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional notes" />
          </div>
          )}

          {/* Routine toggle — hidden for Travel */}
          {form.category !== 'Travel' && (
          <div className="flex items-center gap-2">
            <Switch checked={form.is_routine} onCheckedChange={v => f('is_routine', v)} />
            <Label className="cursor-pointer">Routine item</Label>
          </div>
          )}

          {form.is_routine && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Frequency</Label>
                <Select
                  value={form.frequency_type}
                  onValueChange={v => setForm(p => ({ ...p, frequency_type: v, frequency_config: { ...FREQ_DEFAULTS[v] } }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="p-2 border rounded bg-muted/20">
                <FrequencyConfig form={form} setForm={setForm} />
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">End Date</Label>
                <div className="flex gap-1 mb-1.5">
                  {['fixed', 'relative'].map(t => (
                    <button key={t} type="button"
                      onClick={() => f('end_date_type', t)}
                      className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                        form.end_date_type === t
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}>
                      {t === 'fixed' ? 'Fixed Date' : 'Relative'}
                    </button>
                  ))}
                </div>
                {form.end_date_type === 'fixed' ? (
                  <Input type="date" value={form.end_date} onChange={e => f('end_date', e.target.value)}
                    className={form.end_date ? 'time-filled' : ''} />
                ) : (
                  <div className="flex gap-2 items-center">
                    <Input type="number" min="1" step="1" value={form.end_date_rel_value}
                      onChange={e => f('end_date_rel_value', e.target.value)}
                      placeholder="e.g. 30" className="w-24" />
                    <Select value={form.end_date_rel_unit} onValueChange={v => f('end_date_rel_unit', v)}>
                      <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['days','weeks','months','years'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">from start</span>
                  </div>
                )}
                {form.end_date_type === 'relative' && form.end_date_rel_value && form.start_date && (
                  <p className="text-xs text-muted-foreground">= {computeEndDate()}</p>
                )}
              </div>
            </div>
          )}

          {/* Billable toggle (+ Cost — same format as Care Catalog) */}
          <div className="flex items-center gap-2">
            <Switch checked={form.billable} onCheckedChange={v => f('billable', v)} />
            <Label className="cursor-pointer">Billable</Label>
          </div>

          {form.billable && (
            <div className="space-y-1">
              <Label>Cost per unit</Label>
              <div className="flex gap-2">
                <Select value={form.cost_currency || 'USD'} onValueChange={v => f('cost_currency', v)}>
                  <SelectTrigger className="w-20 shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" min="0" step="0.01" value={form.cost}
                  onChange={e => f('cost', e.target.value)} placeholder="0.00" className="flex-1" />
              </div>
            </div>
          )}

          {/* Attachments */}
          <div className="space-y-2">
            <Button type="button" variant="outline" className="w-full justify-start gap-2"
              onClick={() => setAttachPickerOpen(true)} data-testid="schedule-care-attachments">
              <Paperclip className="w-4 h-4" /> Upload Attachments
            </Button>
            {attachments.length > 0 && (
              <div className="space-y-1">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1">
                    <span className="truncate">{a.name}</span>
                    <Button variant="ghost" size="icon" className="h-4 w-4 shrink-0"
                      onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving || uploading}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || uploading} data-testid="schedule-care-save"
            className="bg-primary hover:bg-primary/90 text-white">
            {saving || uploading ? 'Saving…' : editing ? 'Update' : 'Schedule'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    <AttachmentPickerSheet
      open={attachPickerOpen}
      onClose={() => setAttachPickerOpen(false)}
      onFiles={(files) => setAttachments(prev => [...prev, ...files.map(f => ({ name: f.name, file: f }))])}
    />
    </>
  );
}
